import { GraphInputHelpers } from '@/fetching/graphql/GraphInputHelpers';
import { buildEpisodeDisciplineLink } from '@/pages/PatientDetailsPage/PatientSchedulingTab/PatientEpisodeView/routes';
import { OrderType } from '@/types/order';
import { Patient } from '@/types/patient';
import { Service } from '@/types/service';
import { Visit, VisitDatetimeField, VisitOutcome, VisitState } from '@/types/visit';
// TODO(ENG-7104): Re-enable VisitRequest import once admissions schedulers are working out of the product.
import { VisitRequestState, VisitRequestWorkflowStep } from '@/types/visitRequest';
import { EpisodeOfCareFilter, Ordering } from 'gql-types';
import moment from 'moment';

import {
    defaultEpisodeOfCareRowToLink,
    defaultEpisodeVisitsRowToLink,
    defaultVisitRequestsRowToLink,
    defaultVisitsRowToLink,
} from '../definitions';
import {
    EpisodeOfCareDashboardContainer,
    OrderDashboardContainer,
    PatientDashboardContainer,
    RootEntity,
    VisitDashboardContainer,
    VisitRequestDashboardContainer,
} from '../types';
import { IDashboardVariationConfig } from './types';

/**
 * ======================================================
 * Agency-specific Composable Filter
 * ======================================================
 */

const HOM_AGENCY_IDS = ['263', '395', '268', '273', '278', '400', '401'];
const AH_AGENCY_IDS = ['104', '109', '114', '388'];

export const NORTH_STAR_HOM_AGENCY_FILTER: EpisodeOfCareFilter = {
    externalIds: {
        type: { exact: 'AGENCY_ID' },
        value: {
            inList: HOM_AGENCY_IDS,
        },
    },
};
export const NORTH_STAR_AH_AGENCY_FILTER: EpisodeOfCareFilter = {
    externalIds: {
        type: { exact: 'AGENCY_ID' },
        value: { inList: AH_AGENCY_IDS },
    },
};

const AH_305_TEAM_FILTER: Visit.Filters = {
    territory: {
        name: { iContains: 'Arbor Hospice - AH305' },
    },
};

const ah305TeamConfig = {
    teamFilter: AH_305_TEAM_FILTER,
    teamName: 'AH305',
    teamKey: 'AH305',
};

const homAgencyConfig = {
    agencyFilter: NORTH_STAR_HOM_AGENCY_FILTER,
    agencyName: 'HOM SE',
    agencyKey: 'HOM',
};
const ahAgencyConfig = {
    agencyFilter: NORTH_STAR_AH_AGENCY_FILTER,
    agencyName: 'Arbor',
    agencyKey: 'AH',
};

function augmentPatientContainerWithTeamVariations({
    patientContainer,
    teamFilter,
    teamName,
    teamKey,
}: {
    patientContainer: PatientDashboardContainer;
    teamFilter: Visit.Filters;
    teamName: string;
    teamKey: string;
}): PatientDashboardContainer {
    return {
        ...patientContainer,
        key: `${patientContainer.key}${teamKey}`,
        label: `${patientContainer.label} (${teamName})`,
        tableConfig: {
            ...patientContainer.tableConfig,
            filters: {
                ...patientContainer.tableConfig.filters,
                visits: {
                    ...patientContainer.tableConfig.filters?.visits,
                    ...teamFilter,
                },
            },
        },
        heavyQuery: true,
        defaultEnabled: false,
    };
}

function augmentPatientContainerWithAgencyVariations({
    patientContainer,
    agencyFilter,
    agencyName,
    agencyKey,
}: {
    patientContainer: PatientDashboardContainer;
    agencyFilter: EpisodeOfCareFilter;
    agencyName: string;
    agencyKey: string;
}): PatientDashboardContainer {
    return {
        ...patientContainer,
        key: `${patientContainer.key}${agencyKey}`,
        label: `${patientContainer.label} (${agencyName})`,
        tableConfig: {
            ...patientContainer.tableConfig,
            filters: {
                ...patientContainer.tableConfig.filters,
                episodes: {
                    ...patientContainer.tableConfig.filters?.episodes,
                    ...agencyFilter,
                },
            },
        },
        heavyQuery: true,
        defaultEnabled: false,
    };
}

function patientContainersByAgency(
    patientContainer: PatientDashboardContainer,
): PatientDashboardContainer[] {
    return [
        patientContainer,
        augmentPatientContainerWithAgencyVariations({
            patientContainer,
            ...homAgencyConfig,
        }),
        augmentPatientContainerWithAgencyVariations({
            patientContainer,
            ...ahAgencyConfig,
        }),
    ];
}

function episodeOfCareContainersByAgency(
    episodeOfCareContainer: EpisodeOfCareDashboardContainer,
): EpisodeOfCareDashboardContainer[] {
    return [
        episodeOfCareContainer,
        augmentEpisodeOfCareContainerWithAgencyVariations({
            episodeOfCareContainer,
            ...homAgencyConfig,
        }),
        augmentEpisodeOfCareContainerWithAgencyVariations({
            episodeOfCareContainer,
            ...ahAgencyConfig,
        }),
    ];
}

function augmentEpisodeOfCareContainerWithAgencyVariations({
    episodeOfCareContainer,
    agencyFilter,
    agencyName,
    agencyKey,
}: {
    episodeOfCareContainer: EpisodeOfCareDashboardContainer;
    agencyFilter: EpisodeOfCareFilter;
    agencyName: string;
    agencyKey: string;
}): EpisodeOfCareDashboardContainer {
    return {
        ...episodeOfCareContainer,
        key: `${episodeOfCareContainer.key}${agencyKey}`,
        label: `${episodeOfCareContainer.label} (${agencyName})`,
        tableConfig: {
            ...episodeOfCareContainer.tableConfig,
            filters: {
                ...episodeOfCareContainer.tableConfig.filters,
                ...agencyFilter,
            },
        },
    };
}

function augmentVisitContainerWithAgencyVariations({
    visitContainer,
    agencyFilter,
    agencyName,
    agencyKey,
}: {
    visitContainer: VisitDashboardContainer;
    agencyFilter: EpisodeOfCareFilter;
    agencyName: string;
    agencyKey: string;
}): VisitDashboardContainer {
    return {
        ...visitContainer,
        key: `${visitContainer.key}${agencyKey}`,
        label: `${visitContainer.label} (${agencyName})`,
        tableConfig: {
            ...visitContainer.tableConfig,
            filters: {
                ...visitContainer.tableConfig.filters,
                episode: {
                    ...visitContainer.tableConfig.filters?.episode,
                    ...agencyFilter,
                },
            },
        },
        heavyQuery: true,
        defaultEnabled: false,
    };
}

function visitContainersByAgency(
    visitContainer: VisitDashboardContainer,
): VisitDashboardContainer[] {
    return [
        visitContainer,
        augmentVisitContainerWithAgencyVariations({
            visitContainer,
            ...homAgencyConfig,
        }),
        augmentVisitContainerWithAgencyVariations({
            visitContainer,
            ...ahAgencyConfig,
        }),
    ];
}

function augmentVisitRequestContainerWithAgencyVariations({
    visitRequestContainer,
    agencyFilter,
    agencyName,
    agencyKey,
}: {
    visitRequestContainer: VisitRequestDashboardContainer;
    agencyFilter: EpisodeOfCareFilter;
    agencyName: string;
    agencyKey: string;
}): VisitRequestDashboardContainer {
    return {
        ...visitRequestContainer,
        key: `${visitRequestContainer.key}${agencyKey}`,
        label: `${visitRequestContainer.label} (${agencyName})`,
        tableConfig: {
            ...visitRequestContainer.tableConfig,
            filters: {
                ...visitRequestContainer.tableConfig.filters,
                episode: {
                    ...visitRequestContainer.tableConfig.filters?.episode,
                    ...agencyFilter,
                },
            },
        },
        heavyQuery: true,
        defaultEnabled: false,
    };
}

function visitRequestContainersByAgency(
    visitRequestContainer: VisitRequestDashboardContainer,
): VisitRequestDashboardContainer[] {
    return [
        visitRequestContainer,
        augmentVisitRequestContainerWithAgencyVariations({
            visitRequestContainer,
            ...homAgencyConfig,
        }),
        augmentVisitRequestContainerWithAgencyVariations({
            visitRequestContainer,
            ...ahAgencyConfig,
        }),
    ];
}

function augmentOrderContainerWithAgencyVariations({
    orderContainer,
    agencyFilter,
    agencyName,
    agencyKey,
}: {
    orderContainer: OrderDashboardContainer;
    agencyFilter: EpisodeOfCareFilter;
    agencyName: string;
    agencyKey: string;
}): OrderDashboardContainer {
    return {
        ...orderContainer,
        key: `${orderContainer.key}${agencyKey}`,
        label: `${orderContainer.label} (${agencyName})`,
        tableConfig: {
            ...orderContainer.tableConfig,
            filters: {
                ...orderContainer.tableConfig.filters,
                episode: {
                    ...orderContainer.tableConfig.filters?.episode,
                    ...agencyFilter,
                },
            },
        },
        heavyQuery: true,
        defaultEnabled: false,
    };
}

function orderContainersByAgency(
    orderContainer: OrderDashboardContainer,
): OrderDashboardContainer[] {
    return [
        orderContainer,
        augmentOrderContainerWithAgencyVariations({
            orderContainer,
            ...homAgencyConfig,
        }),
        augmentOrderContainerWithAgencyVariations({
            orderContainer,
            ...ahAgencyConfig,
        }),
    ];
}

/**
 * ======================================================
 * Reusable Filters
 * ======================================================
 */
export const NORTH_STAR_DEATH_VISIT_FILTER: Visit.Filters = {
    services: {
        externalIds: {
            type: { exact: 'SERVICE_ID' },
            value: {
                inList: ['HSAIDEDEATH', 'HSRNDEATH', 'HSSCDEATH', 'HSSWDEATH'],
            },
        },
    },
};

export const NORTH_STAR_DECEASED_OR_DISCHARGED_PATIENT_FILTER: Patient.Filters = {
    metadata: {
        contains: {
            isDischarged: true,
        },
    },
    OR: {
        metadata: {
            contains: {
                isDeceased: true,
            },
        },
        OR: {
            visits: {
                ...NORTH_STAR_DEATH_VISIT_FILTER,
            },
        },
    },
};

const NORTH_STAR_ADMIT_VISIT_SERVICE_FILTER: Service.Filters = {
    externalIds: {
        value: { inList: ['HSRNADMIT', 'HSRNURGENT'] },
        type: { exact: 'SERVICE_ID' },
    },
};

const IS_ACTIVE_BENEFIT_PERIOD_FILTER: EpisodeOfCareFilter = {
    isActiveByDate: { exact: true },
    isActiveField: { exact: true },
};

const ADMITTED_BENEFIT_PERIOD_FILTER: EpisodeOfCareFilter = {
    ...IS_ACTIVE_BENEFIT_PERIOD_FILTER,
    hasVisits: {
        services: NORTH_STAR_ADMIT_VISIT_SERVICE_FILTER,
        outcome: { exact: VisitOutcome.COMPLETED },
    },
};

const ADMITTED_BENEFIT_PERIOD_1_FILTER: EpisodeOfCareFilter = {
    ...ADMITTED_BENEFIT_PERIOD_FILTER,
    numberInSequence: { exact: 1 },
};

/**
 * ======================================================
 * Container Definitions
 * ======================================================
 */
// const PATIENT_IS_NOT_DISCHARGED_FILTER: Patient.Filters = {
//     NOT: {
//         metadata: {
//             contains: {
//                 isDischarged: true,
//             },
//         },
//     },
// };
// TODO(ENG-7104): Re-enable once admissions schedulers are working out of the product.
// const VISIT_REQUEST_OR_ITS_VISIT_IS_PENDING_FILTER: VisitRequest.Filters = {
//     state: { exact: VisitRequestState.PENDING },
//     OR: {
//         state: { exact: VisitRequestState.FULFILLED },
//         AND: {
//             visit: {
//                 needsClinicians: { exact: true },
//             },
//             OR: {
//                 visit: {
//                     state: { inList: [VisitState.UNSCHEDULED, VisitState.SCHEDULED] },
//                 },
//                 // TODO: Verify the missed visit filter logic is correct before implementing.
//                 // OR: {
//                 //     visit: {
//                 //         outcome: { exact: VisitOutcome.MISSED },
//                 //         requiresReschedule: { exact: true },
//                 //     },
//                 // },
//             },
//         },
//     },
// };
//
// const NORTH_STAR_ADMIT_VISIT_CONTAINER: VisitRequestDashboardContainer = {
//     key: 'northStarRnAdmitVisitRequests',
//     label: 'RN Admits',
//     tableConfig: {
//         entity: RootEntity.VISIT_REQUEST,
//         filters: {
//             patient: {
//                 NOT: NORTH_STAR_DECEASED_OR_DISCHARGED_PATIENT_FILTER,
//             },
//             services: NORTH_STAR_ADMIT_VISIT_SERVICE_FILTER,
//             AND: VISIT_REQUEST_OR_ITS_VISIT_IS_PENDING_FILTER,
//         },
//         ordering: {
//             fulfillmentWindowEndDate: Ordering.ASC,
//         },
//         rowToLink: defaultEpisodeVisitRequestsRowToLink,
//         emptyTableText: 'No RN Admits to schedule.',
//     },
//     defaultEnabled: true,
//     hoverText: 'Upcoming RN Admits that need scheduling.',
// };

const NEW_FREQUENCIES_CONTAINER: OrderDashboardContainer = {
    key: 'northStarNewFrequencies',
    label: 'New Frequencies',
    tableConfig: {
        entity: RootEntity.ORDER,
        filters: {
            patient: {
                NOT: NORTH_STAR_DECEASED_OR_DISCHARGED_PATIENT_FILTER,
            },
            orderType: { exact: OrderType.FREQUENCY },
            isActive: { exact: true },
            maxActiveOccurrences: { gt: 1 },
            activeVisitRequests: {
                workflowStep: { exact: VisitRequestWorkflowStep.NEEDS_SERVICES },
            },
        },
        ordering: {
            fulfillmentWindowStartDate: Ordering.ASC,
        },
        rowToLink: ({
            patient: { id: patientId },
            discipline,
            episode,
        }: {
            patient: { id: string };
            discipline: { id: string } | null;
            episode: { id: string } | null;
        }) => {
            return buildEpisodeDisciplineLink(
                {
                    patientId,
                    episodeId: episode?.id,
                },
                discipline?.id,
            );
        },
        emptyTableText: 'No new frequencies to schedule.',
    },
    defaultEnabled: true,
    hoverText: 'New frequencies that need scheduling.',
};

const PATIENTS_NEEDING_SCHEDULING: EpisodeOfCareDashboardContainer = {
    key: 'northStarPatientsNeedingScheduling',
    label: 'Patients Needing Scheduling',
    tableConfig: {
        entity: RootEntity.EPISODE_OF_CARE,
        filters: {
            patient: {
                NOT: NORTH_STAR_DECEASED_OR_DISCHARGED_PATIENT_FILTER,
            },
            endDate: { gte: GraphInputHelpers.date(moment().startOf('day').subtract(14, 'days')) },
            visitRequests: {
                state: { exact: VisitRequestState.PENDING },
            },
        },
        ordering: {
            startDate: Ordering.ASC,
        },
        rowToLink: defaultEpisodeOfCareRowToLink,
        emptyTableText: 'No patients to schedule.',
    },
    defaultEnabled: true,
    hoverText: 'Patients that need scheduling.',
    heavyQuery: true,
};

const EXPIRING_FULFILLMENT_WINDOW_CONTAINER: VisitRequestDashboardContainer = {
    key: 'northStarExpiringFulfillmentWindow',
    label: 'Expiring Fulfillment Windows',
    tableConfig: {
        entity: RootEntity.VISIT_REQUEST,
        filters: {
            patient: {
                NOT: NORTH_STAR_DECEASED_OR_DISCHARGED_PATIENT_FILTER,
            },
            state: { exact: VisitRequestState.PENDING },
            fulfillmentWindowEndDate: {
                gte: GraphInputHelpers.date(moment().subtract(31, 'days')),
                lte: GraphInputHelpers.date(moment().add(3, 'days')),
            },
        },
        ordering: {
            fulfillmentWindowEndDate: Ordering.ASC,
        },
        rowToLink: defaultVisitRequestsRowToLink,
        emptyTableText: 'No visits with expiring fulfillment windows.',
    },
    defaultEnabled: true,
    hoverText: 'Visits with expiring fulfillment windows.',
};

const NORTH_STAR_INCOMPLETE_CARE_TEAM_CONTAINER: PatientDashboardContainer = {
    key: 'northStarIncompleteCareTeam',
    label: 'Incomplete Care Teams',
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            NOT: {
                ...NORTH_STAR_DECEASED_OR_DISCHARGED_PATIENT_FILTER,
                OR: {
                    careTeams: {
                        clinicians: {
                            id: { isNull: false },
                        },
                    },
                },
            },
            hasEpisodes: {
                isActiveByDate: { exact: true },
                isActiveField: { exact: true },
                hasVisits: {
                    services: NORTH_STAR_ADMIT_VISIT_SERVICE_FILTER,
                    outcome: { exact: VisitOutcome.COMPLETED },
                },
            },
        },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
        emptyTableText: 'No patients with incomplete care teams.',
    },
    defaultEnabled: true,
    hoverText: 'Patients that need care teams assigned.',
};

const NORTH_STAR_UNASSIGNED_VISIT_CONTAINER: VisitDashboardContainer = {
    key: 'northStarUnassignedVisits',
    label: 'Unassigned',
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            patient: {
                NOT: NORTH_STAR_DECEASED_OR_DISCHARGED_PATIENT_FILTER,
            },
            needsClinicians: { exact: true },
            outcome: { exact: VisitOutcome.NONE },
        },
        ordering: {
            latestEndDatetime: Ordering.ASC,
        },
        emptyTableText: 'No unassigned visits at this time',
        rowToLink: defaultVisitsRowToLink,
    },
    hoverText: 'Visits in need of a clinician assignment.',
    defaultEnabled: true,
};

// Note: NORTH_STAR_DEATH_DISCHARGE_WITH_VISITS_CONTAINER is disabled
// for the time being because it is causing massive db load spikes
// const NORTH_STAR_DEATH_DISCHARGE_WITH_VISITS_CONTAINER: PatientDashboardContainer = {
//     key: 'northStarDeathDischargeWithVisits',
//     label: 'Deaths/Discharges with Pending Visits',
//     tableConfig: {
//         entity: RootEntity.PATIENT,
//         filters: {
//             episodes: {
//                 visits: {
//                     outcome: { exact: VisitOutcome.NONE },
//                 },
//                 OR: {
//                     visitRequests: {
//                         state: { exact: VisitRequestState.PENDING },
//                     },
//                 },
//             },
//             AND: NORTH_STAR_DECEASED_OR_DISCHARGED_PATIENT_FILTER,
//         },
//         rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
//         emptyTableText: 'No deaths/discharges require visit cancellation at this time.',
//     },
//     hoverText: 'Visit cancellation required.',
//     defaultEnabled: true,
//     heavyQuery: true,
// };

/**
 * RN 14-Day Requirement Container
 *
 * CMS requires an RN visit every 14 days. This container shows patients who need
 * an RN visit scheduled to stay in compliance.
 *
 * Trigger for Inclusion:
 * - Patient is active with an active benefit period
 * - No RN visit in Completed state within the past 10 days
 * - No RN visits today in a non-terminal or Completed state
 * - No RN visits within the next 4 days in a non-terminal state
 *
 * Trigger for Removal:
 * - An RN visit is in a non-terminal or Completed state today
 * - OR in a non-terminal state within the next 4 days
 * - OR patient is discharged
 *
 * Days Left to Schedule: 0 = today is the last day, -1 (in red) = one day out of compliance
 */
const RN_COMPREHENSIVE_FILTER: Visit.Filters = {
    services: {
        externalIds: {
            value: { exact: 'HSRNCOMP' },
        },
    },
};

const NORTH_STAR_RN_14_DAY_VISIT_CONTAINER: PatientDashboardContainer = {
    key: 'northStarRn14DayVisit',
    label: 'RN 14-Day Requirement',
    defaultEnabled: true,
    hoverText: 'Patients requiring RN visit within 14 day requirement',
    heavyQuery: true,
    tableConfig: {
        entity: RootEntity.PATIENT,
        emptyTableText: 'No patients requiring RN visit within 14-day requirement.',
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
        filters: {
            // Patient is not discharged
            NOT: NORTH_STAR_DECEASED_OR_DISCHARGED_PATIENT_FILTER,
            // Patient has an active benefit period (episode)
            episodes: {
                ...IS_ACTIVE_BENEFIT_PERIOD_FILTER,
            },
            AND: {
                // No RN visit completed in the past 10 days
                NOT: {
                    visits: {
                        ...RN_COMPREHENSIVE_FILTER,
                        outcome: { exact: VisitOutcome.COMPLETED },
                        startDatetime: {
                            range: {
                                start: GraphInputHelpers.datetime(moment().subtract(10, 'days')),
                                end: GraphInputHelpers.datetime(new Date()),
                            },
                        },
                    },
                },
                AND: {
                    // No RN visit today in non-terminal (NONE) or Completed state
                    NOT: {
                        visits: {
                            ...RN_COMPREHENSIVE_FILTER,
                            outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                            startDatetime: {
                                range: {
                                    start: GraphInputHelpers.datetime(moment().startOf('day')),
                                    end: GraphInputHelpers.datetime(moment().endOf('day')),
                                },
                            },
                        },
                    },
                    AND: {
                        // No RN visit in the next 4 days in non-terminal state (NONE = pending/scheduled)
                        NOT: {
                            visits: {
                                ...RN_COMPREHENSIVE_FILTER,
                                outcome: { exact: VisitOutcome.NONE },
                                startDatetime: {
                                    range: {
                                        start: GraphInputHelpers.datetime(
                                            moment().add(1, 'day').startOf('day'),
                                        ),
                                        end: GraphInputHelpers.datetime(
                                            moment().add(4, 'days').endOf('day'),
                                        ),
                                    },
                                },
                            },
                        },
                    },
                },
            },
        },
    },
};

const NORTH_STAR_RECERT_CONTAINER: EpisodeOfCareDashboardContainer = {
    key: 'northStarRecert',
    label: 'Recerts',
    defaultEnabled: true,
    hoverText: 'Upcoming Recerts that need scheduling.',
    tableConfig: {
        entity: RootEntity.EPISODE_OF_CARE,
        emptyTableText: 'No Recerts to schedule.',
        filters: {
            patient: {
                NOT: NORTH_STAR_DECEASED_OR_DISCHARGED_PATIENT_FILTER,
            },
            // Episode ends within the next 30 days
            endDate: {
                range: {
                    start: GraphInputHelpers.date(new Date()),
                    end: GraphInputHelpers.date(moment().add(30, 'days')),
                },
            },
            AND: {
                ...ADMITTED_BENEFIT_PERIOD_1_FILTER,
                OR: {
                    ...IS_ACTIVE_BENEFIT_PERIOD_FILTER,
                    numberInSequence: { gt: 1 },
                },
            },
            // No scheduled or completed recert visit in this episode
            hasVisits: {
                NOT: {
                    services: {
                        externalIds: {
                            value: { exact: 'HSRNRECERT' },
                        },
                    },
                    outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                },
            },
        },
        rowToLink: defaultEpisodeOfCareRowToLink,
    },
};

const NORTH_STAR_HUV_CONTAINER: EpisodeOfCareDashboardContainer = {
    key: 'northStarHuv',
    label: 'HUVs',
    defaultEnabled: true,
    hoverText: 'Upcoming HUVs that need scheduling.',
    tableConfig: {
        entity: RootEntity.EPISODE_OF_CARE,
        emptyTableText: 'No HUVs to schedule.',
        filters: {
            patient: {
                NOT: NORTH_STAR_DECEASED_OR_DISCHARGED_PATIENT_FILTER,
            },
            ...ADMITTED_BENEFIT_PERIOD_FILTER,
            hasVisits: {
                ...ADMITTED_BENEFIT_PERIOD_FILTER.hasVisits,
                NOT: {
                    relatedVisitCondition: {
                        exists: true,
                        filter: {
                            services: {
                                externalIds: { value: { inList: ['HSRNHUV1', 'HSRNHUV2'] } },
                            },
                            outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                        },
                        sameEpisode: true,
                        samePatient: true,
                    },
                },
            },
        },
        ordering: {
            startDate: Ordering.ASC,
        },
        rowToLink: defaultEpisodeOfCareRowToLink,
    },
};

/**
 * SFV (Symptom Follow-up Visit) Container
 *
 * Shows SIA (Symptom Impact Assessment) visits that need a follow-up SFV visit.
 *
 * Trigger for Inclusion:
 * - SIA represented in myUnity (has sfv_required metadata)
 * - AND SFV is not yet Confirmed/Scheduled
 * - OR Scheduled/Confirmed SFV is now Unassigned or Marked as Missed
 *
 * Trigger for Removal:
 * - SFV visit is Confirmed
 * - OR patient is discharged
 */
const NORTH_STAR_SFV_CONTAINER: VisitDashboardContainer = {
    key: 'northStarSfv',
    label: 'SFVs',
    defaultEnabled: true,
    hoverText: 'Follow-up visits required within 2 days of Symptom Impact Assessments.',
    tableConfig: {
        entity: RootEntity.VISIT,
        emptyTableText: 'No SFVs need attention at this time.',
        filters: {
            startDatetime: {
                gte: GraphInputHelpers.datetime(moment().startOf('day').subtract(9, 'days')),
            },
            episode: {
                ...ADMITTED_BENEFIT_PERIOD_1_FILTER,
            },
            // SIA visits that have the sfv_required metadata flag
            metadata: { hasKey: 'sfv_required' },
            // Patient is not discharged
            patient: {
                NOT: NORTH_STAR_DECEASED_OR_DISCHARGED_PATIENT_FILTER,
            },
            // AND does NOT have a confirmed/scheduled SFV within 48 hours
            // (meaning the SFV either doesn't exist, or exists but needs attention)
            relatedVisitCondition: {
                exists: false,
                filter: {
                    // SFV service
                    services: {
                        externalIds: {
                            type: { exact: 'SERVICE_ID' },
                            value: {
                                inList: [
                                    // Might add this condition later, it's another service for missed visit
                                    // that might satisfy the condition
                                    // 'HSSNMISS',
                                    'HSRNMISSEDSFV',
                                    'HSLPNSFVPRN',
                                    'HSLPNSFV',
                                    'HSRNSFVPRN',
                                    'HSRNSFV',
                                ],
                            },
                        },
                    },
                    outcome: {
                        inList: [VisitOutcome.COMPLETED, VisitOutcome.NONE, VisitOutcome.MISSED],
                    },
                },
                // Within 48 hours (2 days) after the SIA visit's effective date
                timeConstraints: [
                    {
                        field: VisitDatetimeField.EFFECTIVE_DATE,
                        relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                        gteOffsetMinutes: 0,
                        lteOffsetMinutes: 60 * 24 * 2, // 2 days
                    },
                ],
                samePatient: true,
                sameEpisode: true,
            },
        },
        rowToLink: defaultEpisodeVisitsRowToLink,
        ordering: {
            startDatetime: Ordering.ASC,
        },
    },
    extraProps: {
        showClinicianColumn: false,
    },
};

/**
 * Patient No-Shows Container
 *
 * Shows visits where the patient did not show up and requires follow-up action.
 *
 * Trigger for Inclusion:
 * - Visit outcome is "Patient Did Not Show"
 * - Visit was not ingested from myUnity with "Missed" status
 * - Visit has not been rescheduled
 *
 * Trigger for Removal:
 * - Visit is assigned to another clinician and its status is Unscheduled, Scheduled, or Confirmed
 */
const NORTH_STAR_PATIENT_NO_SHOWS_CONTAINER: VisitDashboardContainer = {
    key: 'northStarPatientNoShows',
    label: 'Patient No-Shows',
    defaultEnabled: true,
    hoverText: 'Patient no-shows requiring attention',
    tableConfig: {
        entity: RootEntity.VISIT,
        emptyTableText: 'No patient no-shows requiring attention.',
        filters: {
            patient: {
                NOT: NORTH_STAR_DECEASED_OR_DISCHARGED_PATIENT_FILTER,
            },
            // Visit is in review
            state: { exact: VisitState.IN_REVIEW },
            // Visit outcome is "Patient Did Not Show"
            outcome: { exact: VisitOutcome.PATIENT_DID_NOT_SHOW },
        },
        // Sort by visit datetime closest to current datetime
        ordering: {
            startDatetime: Ordering.DESC,
        },
        // Navigate to visit details page
        rowToLink: ({ id }: { id: string }) => `/visits/${id}`,
    },
};

/**
 * ======================================================
 * NorthStar Dashboard Variations
 * ======================================================
 */
export const NORTH_STAR_UAT_DASHBOARD_CONFIG: IDashboardVariationConfig = {
    containers: [
        // TODO(ENG-7104): Re-enable once admissions schedulers are working out of the product.
        // ...visitRequestContainersByAgency(NORTH_STAR_ADMIT_VISIT_CONTAINER),
        ...patientContainersByAgency(NORTH_STAR_INCOMPLETE_CARE_TEAM_CONTAINER),
        ...orderContainersByAgency(NEW_FREQUENCIES_CONTAINER),
        ...episodeOfCareContainersByAgency(PATIENTS_NEEDING_SCHEDULING),
        ...visitContainersByAgency(NORTH_STAR_UNASSIGNED_VISIT_CONTAINER),
        ...episodeOfCareContainersByAgency(NORTH_STAR_HUV_CONTAINER),
        ...visitContainersByAgency(NORTH_STAR_SFV_CONTAINER),
        ...visitRequestContainersByAgency(EXPIRING_FULFILLMENT_WINDOW_CONTAINER),
        // makeContainerDefinition({
        //     type: ContainerDefinitionType.OVERDUE_EPISODE_VISIT,
        //     defaultEnabled: true,
        // }),
        // Note: NORTH_STAR_DEATH_DISCHARGE_WITH_VISITS_CONTAINER is disabled
        // for the time being because it is causing massive db load spikes
        // NORTH_STAR_DEATH_DISCHARGE_WITH_VISITS_CONTAINER,
        ...visitContainersByAgency(NORTH_STAR_PATIENT_NO_SHOWS_CONTAINER),
        ...patientContainersByAgency(NORTH_STAR_RN_14_DAY_VISIT_CONTAINER),
        ...episodeOfCareContainersByAgency(NORTH_STAR_RECERT_CONTAINER),
        // makeContainerDefinition({
        //     type: ContainerDefinitionType.VISIT_SYNC_ISSUES,
        //     defaultEnabled: true,
        //     overrides: {
        //         label: 'Sync Issues',
        //     },
        // }),
    ],
};

export const NORTH_STAR_DASHBOARD_CONFIG: IDashboardVariationConfig = {
    containers: [
        // TODO(ENG-7104): Re-enable once admissions schedulers are working out of the product.
        // ...visitRequestContainersByAgency(NORTH_STAR_ADMIT_VISIT_CONTAINER),
        ...patientContainersByAgency(NORTH_STAR_INCOMPLETE_CARE_TEAM_CONTAINER),
        augmentPatientContainerWithTeamVariations({
            patientContainer: NORTH_STAR_INCOMPLETE_CARE_TEAM_CONTAINER,
            ...ah305TeamConfig,
        }),
        ...orderContainersByAgency(NEW_FREQUENCIES_CONTAINER),
        ...episodeOfCareContainersByAgency(PATIENTS_NEEDING_SCHEDULING),
        ...visitContainersByAgency(NORTH_STAR_UNASSIGNED_VISIT_CONTAINER),
        ...episodeOfCareContainersByAgency(NORTH_STAR_HUV_CONTAINER),
        ...visitContainersByAgency(NORTH_STAR_SFV_CONTAINER),
        ...visitRequestContainersByAgency(EXPIRING_FULFILLMENT_WINDOW_CONTAINER),
        // makeContainerDefinition({
        //     type: ContainerDefinitionType.OVERDUE_EPISODE_VISIT,
        //     defaultEnabled: true,
        // }),
        // Note: NORTH_STAR_DEATH_DISCHARGE_WITH_VISITS_CONTAINER is disabled
        // for the time being because it is causing massive db load spikes
        // NORTH_STAR_DEATH_DISCHARGE_WITH_VISITS_CONTAINER,
        ...visitContainersByAgency(NORTH_STAR_PATIENT_NO_SHOWS_CONTAINER),
        // TODO(ENG-6942): Re-enable once new backend filter is implemented.
        // The current query shows too many patients and is slow.
        // NORTH_STAR_RN_14_DAY_VISIT_CONTAINER,
        ...episodeOfCareContainersByAgency(NORTH_STAR_RECERT_CONTAINER),
        // makeContainerDefinition({
        //     type: ContainerDefinitionType.VISIT_SYNC_ISSUES,
        //     defaultEnabled: true,
        // }),
    ],
};
